/**
 * useVirtualPlan - Hook for managing virtual (temporary) meal plans
 * 
 * Virtual plans are temporary plans stored in localStorage that haven't
 * been saved to the server yet. They're used for:
 * - Try It Out flow for anonymous users
 * - Unsaved edits to existing plans
 * - Temporary plans that may never be saved
 * 
 * Features:
 * - LocalStorage management with 10-plan limit
 * - Virtual to real plan conversion
 * - Replacement tracking
 * - Minimal data storage
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import store from 'store';
import indexBy from 'lodash.indexby';

const STORAGE_KEY = 'virtual-menus';
const MAX_VIRTUAL_PLANS = 10;
const KEEP_KEYS = [
    'items', 'title', 'image', 'uuid', 'type', 'meal_types', 
    'breakfasts', 'lunches', 'dinners', 'snacks', 
    'total_fresh', 'total_pantry', 'replaced_by', 'sizes'
    // Note: 'assets' are NOT stored in localStorage to save space
];

/**
 * Virtual plan management hook
 * 
 * @param {string} planUuid - UUID of the plan to manage
 * @param {Object} options - Configuration options
 * @param {Function} options.onConversionComplete - Callback after successful conversion
 * @param {Function} options.onError - Error handler
 * @returns {Object} Virtual plan management interface
 */
export const useVirtualPlan = (planUuid, options = {}) => {
    const { onConversionComplete, onError = console.error } = options;
    
    // State
    const [virtualPlans, setVirtualPlans] = useState(() => {
        return store.get(STORAGE_KEY) || {};
    });
    const [isConverting, setIsConverting] = useState(false);
    const [conversionError, setConversionError] = useState(null);
    
    // Current virtual plan
    const virtualPlan = useMemo(() => {
        return planUuid ? (virtualPlans[planUuid] || null) : null;
    }, [virtualPlans, planUuid]);
    
    // Check if a plan is virtual
    const isVirtual = useCallback((plan) => {
        if (!plan) return false;
        return !plan.links?.self || plan.type === 'virtual';
    }, []);
    
    // Can convert check
    const canConvert = useMemo(() => {
        return !!(virtualPlan && !virtualPlan.replaced_by && !isConverting);
    }, [virtualPlan, isConverting]);
    
    /**
     * Load virtual plan from localStorage
     */
    const loadVirtualPlan = useCallback((uuid) => {
        const plans = store.get(STORAGE_KEY) || {};
        return plans[uuid] || null;
    }, []);
    
    /**
     * Save/update virtual plan with minimal fields
     */
    const saveVirtualPlan = useCallback((plan) => {
        if (!plan?.uuid) {
            plan.uuid = uuid();
        }
        
        // Filter to only essential fields
        const minimalPlan = {};
        KEEP_KEYS.forEach(key => {
            if (plan[key] !== undefined) {
                minimalPlan[key] = plan[key];
            }
        });
        
        // Add metadata
        minimalPlan.uuid = plan.uuid;
        minimalPlan.type = 'virtual';
        
        // Clean up items
        if (minimalPlan.items) {
            minimalPlan.items = minimalPlan.items.map(item => {
                const cleanItem = { ...item };
                delete cleanItem.leftovers_removed;
                return cleanItem;
            });
        }
        
        // Update storage
        setVirtualPlans(prevPlans => {
            const updated = { ...prevPlans, [plan.uuid]: minimalPlan };
            
            // Enforce max limit - remove oldest plans
            const planList = Object.values(updated).sort((a, b) => 
                new Date(a.updated_at) - new Date(b.updated_at)
            );
            
            if (planList.length > MAX_VIRTUAL_PLANS) {
                const toRemove = planList.slice(0, planList.length - MAX_VIRTUAL_PLANS);
                toRemove.forEach(plan => delete updated[plan.uuid]);
            }
            
            store.set(STORAGE_KEY, updated);
            return updated;
        });
        
        return minimalPlan;
    }, []);
    
    /**
     * Convert virtual plan to real plan
     */
    const convertToRealPlan = useCallback(async (saveFunction) => {
        if (!canConvert) {
            const error = new Error('Cannot convert plan: not virtual or already converting');
            onError(error);
            return null;
        }
        
        setIsConverting(true);
        setConversionError(null);
        
        try {
            // Save to server
            const savedPlan = await saveFunction(virtualPlan);
            
            if (!savedPlan?.uuid) {
                throw new Error('Save function did not return a valid plan');
            }
            
            // Mark virtual plan as replaced
            setVirtualPlans(prevPlans => {
                const updated = { ...prevPlans };
                if (updated[virtualPlan.uuid]) {
                    updated[virtualPlan.uuid].replaced_by = savedPlan.uuid;
                }
                store.set(STORAGE_KEY, updated);
                return updated;
            });
            
            // Notify completion
            if (onConversionComplete) {
                onConversionComplete(savedPlan);
            }
            
            return savedPlan;
        } catch (error) {
            setConversionError(error);
            onError(error);
            throw error;
        } finally {
            setIsConverting(false);
        }
    }, [virtualPlan, canConvert, onConversionComplete, onError]);
    
    /**
     * Delete virtual plan
     */
    const deleteVirtualPlan = useCallback((uuid, replacementUuid = null) => {
        setVirtualPlans(prevPlans => {
            const updated = { ...prevPlans };
            
            if (replacementUuid && updated[uuid]) {
                // Mark as replaced
                updated[uuid].replaced_by = replacementUuid;
            } else {
                // Delete entirely
                delete updated[uuid];
            }
            
            store.set(STORAGE_KEY, updated);
            return updated;
        });
    }, []);
    
    /**
     * Process multiple virtual plans (from API)
     */
    const processVirtualPlans = useCallback((plans = []) => {
        // Assign UUIDs if needed
        const processedPlans = plans.map(plan => ({
            ...plan,
            uuid: plan.uuid || uuid()
        }));
        
        // Update storage
        setVirtualPlans(prevPlans => {
            // Index new plans
            const newIndexed = indexBy(processedPlans, 'uuid');
            
            // Merge with existing
            let merged = { ...prevPlans, ...newIndexed };
            
            // Enforce limit
            const planList = Object.values(merged).sort((a, b) => 
                new Date(a.updated_at || 0) - new Date(b.updated_at || 0)
            );
            
            if (planList.length > MAX_VIRTUAL_PLANS) {
                const toRemove = planList.slice(0, planList.length - MAX_VIRTUAL_PLANS);
                toRemove.forEach(plan => delete merged[plan.uuid]);
            }
            
            // Save minimal data
            Object.keys(merged).forEach(key => {
                const plan = merged[key];
                const minimal = {};
                
                KEEP_KEYS.forEach(k => {
                    if (plan[k] !== undefined) {
                        minimal[k] = plan[k];
                    }
                });
                
                minimal.uuid = plan.uuid;
                minimal.type = 'virtual';
                merged[key] = minimal;
            });
            
            store.set(STORAGE_KEY, merged);
            return merged;
        });
        
        return processedPlans;
    }, []);
    
    /**
     * Get virtual plan count
     */
    const getVirtualPlanCount = useCallback(() => {
        return Object.keys(virtualPlans).length;
    }, [virtualPlans]);
    
    /**
     * Prune old virtual plans
     */
    const pruneOldVirtualPlans = useCallback(() => {
        setVirtualPlans(prevPlans => {
            const planList = Object.values(prevPlans).sort((a, b) => 
                new Date(a.updated_at || 0) - new Date(b.updated_at || 0)
            );
            
            if (planList.length <= MAX_VIRTUAL_PLANS) {
                return prevPlans;
            }
            
            const updated = {};
            const keep = planList.slice(-MAX_VIRTUAL_PLANS);
            keep.forEach(plan => {
                updated[plan.uuid] = plan;
            });
            
            store.set(STORAGE_KEY, updated);
            return updated;
        });
    }, []);
    
    // Sync with localStorage changes from other tabs
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === STORAGE_KEY) {
                const newPlans = e.newValue ? JSON.parse(e.newValue) : {};
                setVirtualPlans(newPlans);
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);
    
    return {
        // State
        virtualPlan,
        isVirtual: isVirtual(virtualPlan),
        isConverting,
        conversionError,
        
        // Operations
        loadVirtualPlan,
        saveVirtualPlan,
        convertToRealPlan,
        deleteVirtualPlan,
        processVirtualPlans,
        
        // Utilities
        checkIfVirtual: isVirtual,
        getVirtualPlanCount,
        pruneOldVirtualPlans,
        
        // Metadata
        canConvert,
        storageInfo: {
            count: getVirtualPlanCount(),
            maxCount: MAX_VIRTUAL_PLANS
        }
    };
};

// Export legacy function names for backward compatibility
export { fetchVirtualPlan, fetchAllVirtualPlans, deleteVirtualPlan, processVirtualPlans } from '@utils/Plans';